import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  User, 
  BookOpen, 
  Award, 
  Star, 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  XCircle,
  FileText,
  Download,
  Bell,
  Settings,
  LogOut,
  GraduationCap,
  Phone,
  Mail,
  MapPin,
  Edit3,
  Eye,
  Search,
  TrendingUp,
  MessageCircle,
  Video,
  Calendar as CalendarIcon,
  CreditCard,
  Users,
  Building,
  Menu,
  X
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

interface Application {
  id: string;
  collegeName: string;
  courseName: string;
  applicationDate: string;
  status: 'pending' | 'approved' | 'rejected' | 'waitlist';
  deadline: string;
  applicationFee: number;
  documents: string[];
  lastUpdate: string;
  counselorNotes?: string;
}

interface Scholarship {
  id: string;
  name: string;
  provider: string;
  amount: number;
  applicationDate: string;
  status: 'applied' | 'shortlisted' | 'awarded' | 'rejected';
  deadline: string;
  eligibility: string[];
  requirements: string[];
  lastUpdate: string;
}

interface PremiumConsultancy {
  id: string;
  consultantName: string;
  consultantSpecialization: string;
  sessionType: 'career-guidance' | 'college-selection' | 'application-review' | 'interview-prep';
  scheduledDate: string;
  duration: number; // in minutes
  status: 'scheduled' | 'completed' | 'cancelled' | 'rescheduled';
  fee: number;
  paymentStatus: 'paid' | 'pending' | 'refunded';
  meetingLink?: string;
  notes?: string;
  rating?: number;
  feedback?: string;
}

const StudentDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const navigate = useNavigate();

  // Get student info from localStorage
  const studentData = JSON.parse(localStorage.getItem('studentAuth') || '{}');
  const currentStudent = studentData?.user || {
    name: 'Demo Student',
    email: 'student@demo.com',
    studentId: 'STU2025001',
    phone: '+91 98765 43210',
    course: 'B.Tech CSE',
    year: '2nd Year'
  };

  // Sample data for applications
  const [applications] = useState<Application[]>([
    {
      id: 'APP001',
      collegeName: 'IIT Delhi',
      courseName: 'B.Tech Computer Science',
      applicationDate: '2025-07-15',
      status: 'pending',
      deadline: '2025-08-30',
      applicationFee: 2500,
      documents: ['Academic Transcripts', 'JEE Score', 'Recommendation Letter'],
      lastUpdate: '2025-08-10',
      counselorNotes: 'Strong profile, good chances of admission'
    },
    {
      id: 'APP002',
      collegeName: 'BITS Pilani',
      courseName: 'B.Tech Computer Science',
      applicationDate: '2025-07-20',
      status: 'approved',
      deadline: '2025-08-25',
      applicationFee: 3000,
      documents: ['Academic Transcripts', 'BITSAT Score', 'Personal Essay'],
      lastUpdate: '2025-08-12'
    },
    {
      id: 'APP003',
      collegeName: 'NIT Trichy',
      courseName: 'B.Tech Information Technology',
      applicationDate: '2025-07-25',
      status: 'waitlist',
      deadline: '2025-09-01',
      applicationFee: 2000,
      documents: ['Academic Transcripts', 'JEE Score'],
      lastUpdate: '2025-08-11'
    }
  ]);

  // Sample data for scholarships
  const [scholarships] = useState<Scholarship[]>([
    {
      id: 'SCH001',
      name: 'Merit Scholarship 2025',
      provider: 'Government of India',
      amount: 50000,
      applicationDate: '2025-06-01',
      status: 'awarded',
      deadline: '2025-07-31',
      eligibility: ['95%+ in 12th', 'Family income < 8 LPA'],
      requirements: ['Income Certificate', 'Academic Records', 'Caste Certificate'],
      lastUpdate: '2025-08-01'
    },
    {
      id: 'SCH002',
      name: 'Tech Excellence Scholarship',
      provider: 'Tech Giants Foundation',
      amount: 100000,
      applicationDate: '2025-07-01',
      status: 'shortlisted',
      deadline: '2025-08-15',
      eligibility: ['Engineering students', 'CGPA > 8.5'],
      requirements: ['Project Portfolio', 'Academic Transcripts', 'Essay'],
      lastUpdate: '2025-08-05'
    },
    {
      id: 'SCH003',
      name: 'Need-Based Scholarship',
      provider: 'Education Trust',
      amount: 75000,
      applicationDate: '2025-07-10',
      status: 'applied',
      deadline: '2025-08-30',
      eligibility: ['Family income < 5 LPA', 'Academic merit'],
      requirements: ['Income Certificate', 'Bank Statements', 'Academic Records'],
      lastUpdate: '2025-07-15'
    }
  ]);

  // Sample data for premium consultancy
  const [consultancySessions] = useState<PremiumConsultancy[]>([
    {
      id: 'CONS001',
      consultantName: 'Dr. Priya Sharma',
      consultantSpecialization: 'Engineering Admissions',
      sessionType: 'career-guidance',
      scheduledDate: '2025-08-15T10:00:00',
      duration: 60,
      status: 'scheduled',
      fee: 2500,
      paymentStatus: 'paid',
      meetingLink: 'https://meet.google.com/xyz-abc-123'
    },
    {
      id: 'CONS002',
      consultantName: 'Prof. Rajesh Kumar',
      consultantSpecialization: 'College Selection',
      sessionType: 'college-selection',
      scheduledDate: '2025-08-08T14:00:00',
      duration: 45,
      status: 'completed',
      fee: 2000,
      paymentStatus: 'paid',
      rating: 5,
      feedback: 'Excellent guidance, very helpful in shortlisting colleges'
    },
    {
      id: 'CONS003',
      consultantName: 'Ms. Anita Patel',
      consultantSpecialization: 'Interview Preparation',
      sessionType: 'interview-prep',
      scheduledDate: '2025-08-20T16:00:00',
      duration: 90,
      status: 'scheduled',
      fee: 3000,
      paymentStatus: 'pending'
    }
  ]);

  const handleLogout = () => {
    localStorage.removeItem('studentAuth');
    navigate('/login');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
      case 'awarded':
      case 'completed':
      case 'paid':
        return 'text-green-600 bg-green-100';
      case 'pending':
      case 'applied':
      case 'scheduled':
        return 'text-yellow-600 bg-yellow-100';
      case 'rejected':
      case 'cancelled':
      case 'refunded':
        return 'text-red-600 bg-red-100';
      case 'waitlist':
      case 'shortlisted':
      case 'rescheduled':
        return 'text-blue-600 bg-blue-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
      case 'awarded':
      case 'completed':
        return <CheckCircle className="h-4 w-4" />;
      case 'pending':
      case 'applied':
      case 'scheduled':
        return <Clock className="h-4 w-4" />;
      case 'rejected':
      case 'cancelled':
        return <XCircle className="h-4 w-4" />;
      case 'waitlist':
      case 'shortlisted':
        return <AlertCircle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  // Calculate dashboard statistics
  const dashboardStats = {
    totalApplications: applications.length,
    approvedApplications: applications.filter(app => app.status === 'approved').length,
    scholarshipsAwarded: scholarships.filter(sch => sch.status === 'awarded').length,
    totalScholarshipAmount: scholarships
      .filter(sch => sch.status === 'awarded')
      .reduce((sum, sch) => sum + sch.amount, 0),
    completedSessions: consultancySessions.filter(sess => sess.status === 'completed').length,
    upcomingSessions: consultancySessions.filter(sess => sess.status === 'scheduled').length
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <motion.div
        initial={{ x: -250 }}
        animate={{ x: sidebarOpen ? 0 : -250 }}
        className="w-64 bg-white shadow-lg fixed left-0 top-0 h-full z-30 overflow-y-auto"
      >
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <User className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h2 className="font-bold text-lg text-gray-900">{currentStudent.name}</h2>
              <p className="text-sm text-gray-600">{currentStudent.studentId}</p>
            </div>
          </div>
        </div>

        <nav className="p-4 space-y-2">
          {[
            { id: 'overview', label: 'Overview', icon: <TrendingUp className="h-5 w-5" /> },
            { id: 'applications', label: 'Applications', icon: <FileText className="h-5 w-5" /> },
            { id: 'scholarships', label: 'Scholarships', icon: <Award className="h-5 w-5" /> },
            { id: 'consultancy', label: 'Premium Consultancy', icon: <Users className="h-5 w-5" /> },
            { id: 'profile', label: 'Profile', icon: <User className="h-5 w-5" /> },
            { id: 'settings', label: 'Settings', icon: <Settings className="h-5 w-5" /> },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors ${
                activeTab === tab.id
                  ? 'bg-blue-100 text-blue-700 border border-blue-200'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {tab.icon}
              <span className="font-medium">{tab.label}</span>
            </button>
          ))}
        </nav>

        <div className="absolute bottom-4 left-4 right-4">
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            <LogOut className="h-4 w-4" />
            <span>Logout</span>
          </button>
        </div>
      </motion.div>

      {/* Main Content */}
      <div className={`flex-1 transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-0'}`}>
        {/* Header */}
        <div className="bg-white shadow-sm border-b border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 rounded-lg hover:bg-gray-100"
              >
                {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
              <h1 className="text-2xl font-bold text-gray-900">Student Dashboard</h1>
            </div>

            <div className="flex items-center space-x-4">
              <button className="relative p-2 text-gray-600 hover:text-gray-900">
                <Bell className="h-6 w-6" />
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  3
                </span>
              </button>
              
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">Welcome back,</span>
                <span className="font-medium text-gray-900">{currentStudent.name}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Dashboard Stats */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-xl p-6 shadow-lg border-l-4 border-blue-500"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-600 text-sm font-medium">Total Applications</p>
                      <p className="text-2xl font-bold text-gray-900 mt-2">{dashboardStats.totalApplications}</p>
                      <p className="text-green-600 text-sm mt-1">
                        {dashboardStats.approvedApplications} approved
                      </p>
                    </div>
                    <FileText className="h-12 w-12 text-blue-600" />
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-white rounded-xl p-6 shadow-lg border-l-4 border-green-500"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-600 text-sm font-medium">Scholarships</p>
                      <p className="text-2xl font-bold text-gray-900 mt-2">{dashboardStats.scholarshipsAwarded}</p>
                      <p className="text-green-600 text-sm mt-1">
                        ₹{dashboardStats.totalScholarshipAmount.toLocaleString()} awarded
                      </p>
                    </div>
                    <Award className="h-12 w-12 text-green-600" />
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="bg-white rounded-xl p-6 shadow-lg border-l-4 border-purple-500"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-600 text-sm font-medium">Consultancy Sessions</p>
                      <p className="text-2xl font-bold text-gray-900 mt-2">{dashboardStats.completedSessions}</p>
                      <p className="text-blue-600 text-sm mt-1">
                        {dashboardStats.upcomingSessions} upcoming
                      </p>
                    </div>
                    <Users className="h-12 w-12 text-purple-600" />
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="bg-white rounded-xl p-6 shadow-lg border-l-4 border-yellow-500"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-600 text-sm font-medium">Academic Year</p>
                      <p className="text-2xl font-bold text-gray-900 mt-2">{currentStudent.year}</p>
                      <p className="text-gray-600 text-sm mt-1">{currentStudent.course}</p>
                    </div>
                    <GraduationCap className="h-12 w-12 text-yellow-600" />
                  </div>
                </motion.div>
              </div>

              {/* Recent Activity */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Recent Applications */}
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Recent Applications</h3>
                  <div className="space-y-4">
                    {applications.slice(0, 3).map((app) => (
                      <div key={app.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium text-gray-900">{app.collegeName}</p>
                          <p className="text-sm text-gray-600">{app.courseName}</p>
                        </div>
                        <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(app.status)}`}>
                          {getStatusIcon(app.status)}
                          <span className="ml-1 capitalize">{app.status}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                  <button 
                    onClick={() => setActiveTab('applications')}
                    className="w-full mt-4 text-blue-600 hover:text-blue-800 font-medium"
                  >
                    View All Applications →
                  </button>
                </div>

                {/* Upcoming Sessions */}
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Upcoming Sessions</h3>
                  <div className="space-y-4">
                    {consultancySessions
                      .filter(session => session.status === 'scheduled')
                      .slice(0, 3)
                      .map((session) => (
                        <div key={session.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-medium text-gray-900">{session.consultantName}</p>
                            <p className="text-sm text-gray-600">{session.sessionType.replace('-', ' ')}</p>
                            <p className="text-xs text-gray-500">
                              {new Date(session.scheduledDate).toLocaleDateString()} at{' '}
                              {new Date(session.scheduledDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                          <button className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
                            Join
                          </button>
                        </div>
                      ))}
                  </div>
                  <button 
                    onClick={() => setActiveTab('consultancy')}
                    className="w-full mt-4 text-blue-600 hover:text-blue-800 font-medium"
                  >
                    View All Sessions →
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Applications Tab */}
          {activeTab === 'applications' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">My Applications</h2>
                <Link
                  to="/colleges"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Apply to New College
                </Link>
              </div>

              {/* Search and Filter */}
              <div className="flex items-center space-x-4">
                <div className="relative flex-1">
                  <Search className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search applications..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full"
                  />
                </div>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  aria-label="Filter applications by status"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="waitlist">Waitlist</option>
                </select>
              </div>

              {/* Applications List */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {applications.map((app) => (
                  <motion.div
                    key={app.id}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 hover:shadow-xl transition-shadow"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">{app.collegeName}</h3>
                        <p className="text-gray-600">{app.courseName}</p>
                      </div>
                      <span className={`inline-flex items-center px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(app.status)}`}>
                        {getStatusIcon(app.status)}
                        <span className="ml-1 capitalize">{app.status}</span>
                      </span>
                    </div>

                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Application Date:</span>
                        <span className="font-medium">{new Date(app.applicationDate).toLocaleDateString()}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Deadline:</span>
                        <span className="font-medium text-red-600">{new Date(app.deadline).toLocaleDateString()}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Application Fee:</span>
                        <span className="font-medium">₹{app.applicationFee.toLocaleString()}</span>
                      </div>
                    </div>

                    <div className="mb-4">
                      <p className="text-sm text-gray-600 mb-2">Required Documents:</p>
                      <div className="flex flex-wrap gap-2">
                        {app.documents.map((doc, index) => (
                          <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                            {doc}
                          </span>
                        ))}
                      </div>
                    </div>

                    {app.counselorNotes && (
                      <div className="mb-4 p-3 bg-green-50 rounded-lg">
                        <p className="text-sm text-green-800">
                          <strong>Counselor Notes:</strong> {app.counselorNotes}
                        </p>
                      </div>
                    )}

                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">
                        Last updated: {new Date(app.lastUpdate).toLocaleDateString()}
                      </span>
                      <div className="flex space-x-2">
                        <button 
                          className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg"
                          title="View application details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button 
                          className="p-2 text-green-600 hover:bg-green-100 rounded-lg"
                          title="Edit application"
                        >
                          <Edit3 className="h-4 w-4" />
                        </button>
                        <button 
                          className="p-2 text-purple-600 hover:bg-purple-100 rounded-lg"
                          title="Download application"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* Scholarships Tab */}
          {activeTab === 'scholarships' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">My Scholarships</h2>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {scholarships.map((scholarship) => (
                  <motion.div
                    key={scholarship.id}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 hover:shadow-xl transition-shadow"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">{scholarship.name}</h3>
                        <p className="text-gray-600">{scholarship.provider}</p>
                      </div>
                      <span className={`inline-flex items-center px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(scholarship.status)}`}>
                        {getStatusIcon(scholarship.status)}
                        <span className="ml-1 capitalize">{scholarship.status}</span>
                      </span>
                    </div>

                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Amount:</span>
                        <span className="font-bold text-green-600">₹{scholarship.amount.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Applied On:</span>
                        <span className="font-medium">{new Date(scholarship.applicationDate).toLocaleDateString()}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Deadline:</span>
                        <span className="font-medium text-red-600">{new Date(scholarship.deadline).toLocaleDateString()}</span>
                      </div>
                    </div>

                    <div className="mb-4">
                      <p className="text-sm text-gray-600 mb-2">Eligibility:</p>
                      <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                        {scholarship.eligibility.map((criteria, index) => (
                          <li key={index}>{criteria}</li>
                        ))}
                      </ul>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">
                        Last updated: {new Date(scholarship.lastUpdate).toLocaleDateString()}
                      </span>
                      <div className="flex space-x-2">
                        <button 
                          className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg"
                          title="View scholarship details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button 
                          className="p-2 text-purple-600 hover:bg-purple-100 rounded-lg"
                          title="Download scholarship documents"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* Premium Consultancy Tab */}
          {activeTab === 'consultancy' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">Premium Consultancy</h2>
                <Link
                  to="/premium-services"
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Book New Session
                </Link>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {consultancySessions.map((session) => (
                  <motion.div
                    key={session.id}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 hover:shadow-xl transition-shadow"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">{session.consultantName}</h3>
                        <p className="text-gray-600">{session.consultantSpecialization}</p>
                        <p className="text-sm text-blue-600 capitalize">{session.sessionType.replace('-', ' ')}</p>
                      </div>
                      <span className={`inline-flex items-center px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(session.status)}`}>
                        {getStatusIcon(session.status)}
                        <span className="ml-1 capitalize">{session.status}</span>
                      </span>
                    </div>

                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Date & Time:</span>
                        <span className="font-medium">
                          {new Date(session.scheduledDate).toLocaleDateString()} at{' '}
                          {new Date(session.scheduledDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Duration:</span>
                        <span className="font-medium">{session.duration} minutes</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Fee:</span>
                        <span className="font-medium">₹{session.fee.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Payment:</span>
                        <span className={`font-medium ${
                          session.paymentStatus === 'paid' ? 'text-green-600' : 
                          session.paymentStatus === 'pending' ? 'text-yellow-600' : 'text-red-600'
                        }`}>
                          {session.paymentStatus}
                        </span>
                      </div>
                    </div>

                    {session.rating && (
                      <div className="mb-4 p-3 bg-yellow-50 rounded-lg">
                        <div className="flex items-center mb-2">
                          <span className="text-sm font-medium text-gray-700">Your Rating:</span>
                          <div className="flex ml-2">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`h-4 w-4 ${
                                  i < session.rating! ? 'text-yellow-400 fill-current' : 'text-gray-300'
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                        {session.feedback && (
                          <p className="text-sm text-gray-700">{session.feedback}</p>
                        )}
                      </div>
                    )}

                    <div className="flex items-center justify-between">
                      {session.status === 'scheduled' && session.meetingLink && (
                        <a
                          href={session.meetingLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
                        >
                          <Video className="h-4 w-4" />
                          <span>Join Meeting</span>
                        </a>
                      )}
                      
                      <div className="flex space-x-2 ml-auto">
                        {session.status === 'scheduled' && (
                          <button 
                            className="p-2 text-yellow-600 hover:bg-yellow-100 rounded-lg"
                            title="Reschedule session"
                          >
                            <CalendarIcon className="h-4 w-4" />
                          </button>
                        )}
                        <button 
                          className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg"
                          title="Send message to consultant"
                        >
                          <MessageCircle className="h-4 w-4" />
                        </button>
                        {session.paymentStatus === 'pending' && (
                          <button 
                            className="p-2 text-green-600 hover:bg-green-100 rounded-lg"
                            title="Make payment"
                          >
                            <CreditCard className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-900">My Profile</h2>
              
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center space-x-6 mb-6">
                  <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center">
                    <User className="h-12 w-12 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">{currentStudent.name}</h3>
                    <p className="text-gray-600">{currentStudent.studentId}</p>
                    <p className="text-blue-600">{currentStudent.course} - {currentStudent.year}</p>
                  </div>
                  <button className="ml-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                    Edit Profile
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-4">Contact Information</h4>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-3">
                        <Mail className="h-5 w-5 text-gray-400" />
                        <span className="text-gray-700">{currentStudent.email}</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Phone className="h-5 w-5 text-gray-400" />
                        <span className="text-gray-700">{currentStudent.phone}</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <MapPin className="h-5 w-5 text-gray-400" />
                        <span className="text-gray-700">Mumbai, Maharashtra, India</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-4">Academic Information</h4>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-3">
                        <GraduationCap className="h-5 w-5 text-gray-400" />
                        <span className="text-gray-700">{currentStudent.course}</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <BookOpen className="h-5 w-5 text-gray-400" />
                        <span className="text-gray-700">{currentStudent.year}</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Building className="h-5 w-5 text-gray-400" />
                        <span className="text-gray-700">Engineering College</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-900">Settings</h2>
              
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Notification Preferences</h3>
                <div className="space-y-4">
                  <label className="flex items-center">
                    <input type="checkbox" className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50" defaultChecked />
                    <span className="ml-2 text-gray-700">Email notifications for application updates</span>
                  </label>
                  <label className="flex items-center">
                    <input type="checkbox" className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50" defaultChecked />
                    <span className="ml-2 text-gray-700">SMS notifications for important deadlines</span>
                  </label>
                  <label className="flex items-center">
                    <input type="checkbox" className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50" />
                    <span className="ml-2 text-gray-700">Push notifications for scholarship opportunities</span>
                  </label>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Privacy Settings</h3>
                <div className="space-y-4">
                  <label className="flex items-center">
                    <input type="checkbox" className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50" defaultChecked />
                    <span className="ml-2 text-gray-700">Allow consultants to view my profile</span>
                  </label>
                  <label className="flex items-center">
                    <input type="checkbox" className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50" />
                    <span className="ml-2 text-gray-700">Show my success stories to other students</span>
                  </label>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Actions</h3>
                <div className="space-y-4">
                  <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                    Change Password
                  </button>
                  <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
                    Download My Data
                  </button>
                  <button className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
                    Delete Account
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentDashboard;
